import { Injectable, OnDestroy } from '@angular/core';

import { of, Observable, BehaviorSubject, combineLatest, defer, Subscription, forkJoin, Subject} from 'rxjs';
import { map, switchMap, shareReplay, catchError, finalize, distinctUntilChanged, filter, tap } from 'rxjs/operators';

import { SortOrderServiceInterface } from '@Common/Misc/services/sort-order.service.interface';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { LocalizedEnum } from '@api/journodex/model/localized-enum';
import { DimQuestion } from '../models/vi-dimension-card.model';
import { VisibilityMode } from '../components/ViChartDrillDownDialog/vi-chart-drill-down.dialog.component';
import { MentionsSearchResponseModel } from '@api/mentions/model/mentions-search-response.model';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { MediaTypeModel } from '@Monitor/Shared/models/media-type.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { NeoFacetTermFieldApiEnum } from '@api/mentions/enum/neo-facet-term-field-api.enum';
import { BucketFunctionApiEnum } from '@api/mentions/enum/bucket-function-api.enum';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { ViMonitorService } from './vi-monitor.service';
import { ViMonitor } from '../models/vi-monitor.model';
import { VisibilityIntelligenceSummaryChartTypesEnum as ViChartTypeEnum } from '../enums/vi-summary-chart-types.enum';
import { BucketResponseModel } from '@api/mentions/model/bucket-response.model';
import { AgprMath } from '@Common/Misc/helpers/math/math-helper';
import { MentionsApi } from '@api/mentions/mentions.api';

export interface DrillDownSearchParams {
    date: DateQueryModel;
    previousDate: DateQueryModel | null;
    chartType: ViChartTypeEnum;
}

const BY_QUESTION_KEY = 'BY_QUESTION';


@Injectable()
export class ViChartDrillDownService implements SortOrderServiceInterface, OnDestroy {

    readonly facetFieldByChartType = new Map<ViChartTypeEnum, NeoFacetTermFieldApiEnum>([
        [ ViChartTypeEnum.BRAND_SCORE,       NeoFacetTermFieldApiEnum.VI_BRAND_SCORE       ],
        [ ViChartTypeEnum.VISIBILITY_SCORE,  NeoFacetTermFieldApiEnum.VI_ORGANIC_INCLUSION ],
        [ ViChartTypeEnum.PRESENCE_RATE,     NeoFacetTermFieldApiEnum.VI_BRAND_MENTIONED   ],
        [ ViChartTypeEnum.RANK_WHEN_LISTED,  NeoFacetTermFieldApiEnum.VI_MENTION_RANK      ],
        [ ViChartTypeEnum.SENTIMENT_SCORE,   NeoFacetTermFieldApiEnum.VI_SENTIMENT_SCORE   ],
        [ ViChartTypeEnum.ACCURANCY_SCORE,   NeoFacetTermFieldApiEnum.VI_FACTUAL_ACCURACY  ],
        [ ViChartTypeEnum.RISK_RATE,         NeoFacetTermFieldApiEnum.VI_RISK_FLAG         ],
        [ ViChartTypeEnum.CITATION_PER_SCAN, NeoFacetTermFieldApiEnum.VI_CITATION_COUNT    ],
        // [ ViChartTypeEnum.COMPETITIVE_STRENGTH, NeoFacetTermFieldApiEnum.      ], // @TODO
    ]);


    readonly sortFields: LocalizedEnum[] = [
        { enumValue: 'VALUE', displayString: 'Vi.sort.value' }
    ];


    private currentMonitor: ViMonitor;

    private subscriptions: Subscription[] = [];

    private params$ = new BehaviorSubject<DrillDownSearchParams | null>(null);

    // Client-side filters (changing these only re-derives display results)
    private visibility$ = new BehaviorSubject<VisibilityMode>(VisibilityMode.SHOW);
    private sortOrder$ = new BehaviorSubject<SortOrderEnum>(SortOrderEnum.ascending);
    private sortField$  = new BehaviorSubject<LocalizedEnum>(this.sortFields[0]);

    // Request state
    private loading$ = new BehaviorSubject<boolean>(false);
    private error$ = new Subject<void>();


    private readonly results$: Observable<DimQuestion[]> = this.params$.pipe(
        filter((params): params is DrillDownSearchParams => params !== null),
        distinctUntilChanged((a, b) =>
            DateQueryModel.equals(a.date, b.date) &&
            DateQueryModel.equals(a.previousDate, b.previousDate) &&
            a.chartType === b.chartType
        ),
        switchMap(params => {
            this.loading$.next(true);

            return this.searchWithDelta(params).pipe(
                catchError(() => {
                    this.error$.next();
                    return of([] as DimQuestion[]);
                }),
                finalize(() => this.loading$.next(false))
            );
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    readonly displayResults$: Observable<DimQuestion[]> = combineLatest([
        this.results$,
        this.sortOrder$,
        this.visibility$
    ]).pipe(
        map(([results, order, visibility]) => {
            const visible = visibility === VisibilityMode.HIDE
                ? results.filter(q => q.score > 0)
                : results;
            const dir = order === SortOrderEnum.ascending ? 1 : -1;
            return [...visible].sort((a, b) => (a.score - b.score) * dir);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );


    constructor(
        private viMonitorService: ViMonitorService,
        private mentionsApi: MentionsApi,
    ) {
        this.subscriptions.push(
            this.viMonitorService.currentResource$.pipe(
                filter(x => !!x)
            ).subscribe(monitor => {
                this.currentMonitor = monitor;
            })
        );
    }


    /** Call once on load with all defaults; produces a single search. */
    initialize(params: DrillDownSearchParams): void {
        this.params$.next(params);
    }

    /** Date and previous date change together, so they're set in one emission. */
    setDate(date: DateQueryModel, previousDate: DateQueryModel | null): void {
        this.patchParams({ date, previousDate });
    }

    setChartType(chartType: ViChartTypeEnum): void {
        this.patchParams({ chartType });
    }


    private patchParams(patch: Partial<DrillDownSearchParams>): void {
        const current = this.params$.value;
        if (!current) {
            console.warn('ViChartDrillDownService: initialize() must be called before updating params');
            return;
        }
        this.params$.next({ ...current, ...patch });
    }

    private searchWithDelta(params: DrillDownSearchParams): Observable<DimQuestion[]> {
        const current$ = this.fetchScores(params.date, params.chartType);
        const previous$ = params.previousDate
            ? this.fetchScores(params.previousDate, params.chartType)
            : of(new Map<string, number>());

        return forkJoin([current$, previous$]).pipe(
            tap(([current, previous]) => {
                // One notification per search, even if both calls failed
                if (current === null || previous === null) {
                    this.error$.next();
                }
            }),
            map(([current, previous]) => this.buildQuestions(current, previous, params.chartType))
        );
    }

    /** Runs one search and returns rounded average score per question id. */
    private fetchScores(dateQuery: DateQueryModel, chartType: ViChartTypeEnum): Observable<Map<string, number|null>> {
        if (!dateQuery) {
            return of(null);
        }

        const searchCriteria = this.buildCriteria(chartType);
        searchCriteria.query.dateQuery = dateQuery;
        // defer so each search sets its own criteria and date right before it runs
        return defer(() => {
            return this.mentionsApi.viSearchAccount(searchCriteria.getApiEntity());
        }).pipe(
            map(response => this.parseScores(response, chartType)),
            catchError(err => {
                console.error('Score search failed', err);
                return of(null);
            })
        );
    }

    private buildCriteria(chartType: ViChartTypeEnum): MentionSearchCriteriaModel {
        const criteria = new MentionSearchCriteriaModel(undefined, true);
        criteria.isMonitorSearch = true;
        // @HACK weird b/e LLM_RESPONSE is a MediaTypeEnum
        criteria.query.includedMediaTypes = [new MediaTypeModel('LLM_RESPONSE' as MediaTypeEnum)];
        criteria.query.aiAgent.include = [this.currentMonitor.agentId];

        const neoFacetTerm = this.facetFieldByChartType.get(chartType);

        if (chartType === ViChartTypeEnum.PRESENCE_RATE || chartType === ViChartTypeEnum.RISK_RATE) {
            criteria.neoFacets = [
                {
                    termField: NeoFacetTermFieldApiEnum.AI_AGENT_SOURCE_ID,
                    key: BY_QUESTION_KEY,
                    plainBucketRequests: [
                        {
                            field: neoFacetTerm,
                            key: `${neoFacetTerm}_${BY_QUESTION_KEY}`,
                        }
                    ]
                },
            ];
        } else {
            criteria.neoFacets = [
                {
                    termField: NeoFacetTermFieldApiEnum.AI_AGENT_SOURCE_ID,
                    key: BY_QUESTION_KEY,
                    aggregateBucketRequests: [
                        {
                            field: neoFacetTerm,
                            key: `${neoFacetTerm}_${BY_QUESTION_KEY}`,
                            function: BucketFunctionApiEnum.INDIVIDUAL_AVG
                        },
                        {
                            field: neoFacetTerm,
                            key: `${neoFacetTerm}_COUNT_${BY_QUESTION_KEY}`,
                            function: BucketFunctionApiEnum.INDIVIDUAL_COUNT
                        },
                    ]
                }
            ];
        }


        return criteria;
    }

    private parseScores(response: MentionsSearchResponseModel, chartType: ViChartTypeEnum): Map<string, number|null> {
        const neoFacetTerm = this.facetFieldByChartType.get(chartType);
        const result = response.neoFacetResults?.find(x => x.key === BY_QUESTION_KEY);
        const scores = new Map<string, number>();

        for (const bucket of result?.buckets ?? []) {
            const questionId: string = bucket.termValue;

            if (chartType === ViChartTypeEnum.PRESENCE_RATE || chartType === ViChartTypeEnum.RISK_RATE) {
                const booleanFacetResults = bucket.buckets?.[`${neoFacetTerm}_${BY_QUESTION_KEY}`] as BucketResponseModel[];

                let totalTrue = 0, totalFalse = 0;
                for (const facet of booleanFacetResults) {
                    if (facet.termValue === 'TRUE') {
                        totalTrue += facet.count;
                    } else if (facet.termValue === 'FALSE') {
                        totalFalse += facet.count;
                    }
                };
                const denominator = totalTrue + totalFalse;

                if (denominator === 0) {
                    scores.set(questionId, null);
                } else {
                    scores.set(questionId, ((totalTrue / denominator) * 100));
                }

            } else {
                const count = bucket.buckets?.[`${neoFacetTerm}_COUNT_${BY_QUESTION_KEY}`]?.[0]?.count;
                const avg = bucket.buckets?.[`${neoFacetTerm}_${BY_QUESTION_KEY}`]?.[0]?.count;

                if (count > 0 && avg != null) {
                    scores.set(questionId, (Math.round(avg * 100) / 100));
                } else {
                    scores.set(questionId, null);
                }
            }
        }

        return scores;
    }

    /**
     * @param current - map can be null, in which case search failed
     * @param previous - map can be null, in which case search failed
     * @returns Enriched Questions
     */
    private buildQuestions(
        current: Map<string, number|null>,
        previous: Map<string, number|null>,
        chartType: ViChartTypeEnum
    ): DimQuestion[] {
        return this.currentMonitor.questions.map(question => {
            const score = current?.get(question.id);
            const previousScore = previous?.get(question.id);

            let delta = null;

            if (score != null && previousScore != null) {
                if (score === 0 && previousScore === 0) {
                    delta = 0;
                } else if (previousScore !== 0) {

                    if (chartType === ViChartTypeEnum.RANK_WHEN_LISTED) {
                        delta = Math.round((score - previousScore) * 100) / 100;
                    } else {
                        delta = AgprMath.percentageChange(score, previousScore);
                    }
                }
            }

            let finalScore = null;

            if (current !== null && score !== null) {
                finalScore = Math.round(score * 100) / 100;
            }

            return {
                ...question,
                score: finalScore,
                delta: delta
            };
        });
    }


    // --- Request state ---
    isLoading(): Observable<boolean> {
        return this.loading$.asObservable();
    }

    getError(): Observable<void> {
        return this.error$.asObservable();
    }


    // --- Other filters ---

    getVisibility(): Observable<VisibilityMode> {
        return this.visibility$.asObservable();
    }

    setVisibility(mode: VisibilityMode) {
        this.visibility$.next(mode);
    }


    // --- SortOrderServiceInterface ---

    getSortableFields(): Observable<any[]> {
        return of(this.sortFields);
    }

    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$.asObservable();
    }

    getSortField(): Observable<any> {
        return this.sortField$.asObservable();
    }


    setSortOrder(order: SortOrderEnum): void {
        this.sortOrder$.next(order);
    }

    setSortField(field: any): void {
        this.sortField$.next(field);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
